import * as Yup from "yup";
import { WasteMassValidator } from "../../../../domain/Orders/CreatePurchaseOrderValidator";
import { WASTE_EXCHANGE_TYPE } from "../../../../domain/WasteExchangeType";
import { formatWasteExchangeContainerName } from "../../../../_helpers/wasteExchangeContainerName";

export function WeighbridgeTicketsFormProvider({ order, formControls, setProtocolCompleted }) {
  const { contractorDetails, wastesDetails } = order || {};
  const { weighbridgeTickets, wasteExchanges } = wastesDetails || {};

  const mapToTicket = (x) => {
    const wasteExchange = wasteExchanges?.find(w => w.wasteExchangeId === x.wasteExchangeId);
    const isContainer = wasteExchange?.isContainer ?? false;

    return {
      ticketId: x.id ?? null,
      ticketNumber: x.ticketNumber ?? "",
      netMass: x.netMass?.toFixed(4) ?? "",
      wastes: x.wasteDetails?.map(w => ({
        id: w.id,
        ...w.wasteInfo,
        wasteMass: w.wasteInfo.wasteMass?.toFixed(4) ?? "",
        wasteEntityId: w.wasteInfo.wasteKindId ?? w.wasteInfo.wasteId,
        externalWeighedWasteId: w.externalWeighedWasteId,
        isQuickWeighed: w.isQuickWeighed,
        externalWeighedWasteDescription: w.externalWeighedWasteId && w.isQuickWeighed ? `${w.externalWeighedWasteId} (SW)` : ""
      })) ?? [],
      remarks: x.remarks,
      exchangeType: isContainer ? WASTE_EXCHANGE_TYPE.CONTAINER_EXCHANGE : WASTE_EXCHANGE_TYPE.LOADING,
      wasteExchange: isContainer && wasteExchange ? {
        wasteExchangeId: wasteExchange.wasteExchangeId,
        orderExchangeContainerName: formatWasteExchangeContainerName(wasteExchange),
        usedContainerId: wasteExchange.usedContainerId
      } : null,
      loadingWasteExchangeId: !isContainer ? x.wasteExchangeId : null,
      rejectedWastes: x.rejectedWasteDetails?.map(r => ({
        id: r.id,
        weightDifference: { weightDifferenceId: r.weightDifferenceId, name: r.weightDifferenceName },
        wasteMass: r.wasteMass?.toFixed(4) ?? ""
      })) ?? [],
      isProtocolNeeded: x.isProtocolNeeded,
      isProtocolCompleted: x.isProtocolCompleted,
      externalTicketId: x.externalTicketId,
      externalData: x.externalData
    };
  };
  const defaultTickets = weighbridgeTickets?.map(mapToTicket) ?? [];
  const defaultValues = {
    weighbridgeTickets: defaultTickets.length > 0 ? defaultTickets : [],
    orderContext: {
      addressId: contractorDetails?.addressId ?? order?.addressId,
      transactionType: order?.transactionType,
      externalContractorIds: contractorDetails?.externalWeighingLinks?.map(link => link.id) ?? [],
      wasteExchanges: wasteExchanges,
      setProtocolCompleted: setProtocolCompleted
    },
    overrideWasteDetails: true
  };

  const formControlsElement = (
    <div className="domain-control">
      {formControls}
    </div>
  );

  const MASS_SUM_TOLERANCE = 0.0001;
  const ZERO_WEIGHED_MAX_WASTE_MASS = 0.0200;

  const zeroWeighedWasteSchema = Yup.object().shape({
    wasteMass: Yup.string()
      .nullable()
      .test(
        "max-mass-for-zero-weighed-ticket",
        `Masa towaru nie może być większa niż ${ZERO_WEIGHED_MAX_WASTE_MASS} dla zerowego kwitu`,
        (value) => {
          if (value === null || value === undefined || value === "") return true;
          const numericValue = parseFloat(String(value).replace(",", "."));
          if (isNaN(numericValue)) return true; // format handled elsewhere
          return numericValue <= ZERO_WEIGHED_MAX_WASTE_MASS;
        }
      )
  });

  const validationSchema = Yup.object().shape({
    weighbridgeTickets: Yup.array().of(
      Yup.object().shape({
        ticketNumber: Yup.string().nullable().required("Numer kwitu jest wymagany"),
        netMass: Yup.number()
          .typeError("Masa netto jest wymagana")
          .required("Masa netto jest wymagana")
          .test(
            "net-mass-positive-unless-zero-weighed",
            "Masa netto musi być większa od 0",
            function (netMassValue) {
              const { isZeroWeighedTicket } = this.parent ?? {};
              if (isZeroWeighedTicket) {
                return true; // zero weighed ticket allows non-positive net mass
              }

              const netMassNum = parseFloat(netMassValue);
              if (isNaN(netMassNum)) return true; // handled by required/type validators

              return netMassNum > 0;
            }
          )
          .test(
            "waste-mass-sum-matches-net-mass",
            function () {
              return `Suma mas towarów musi być równa masie netto`;
            },
            function (netMassValue) {
              const { isProtocolNeeded, isZeroWeighedTicket, wastes, rejectedWastes } = this.parent ?? {};
              if (isZeroWeighedTicket) {
                return true; // zero weighed ticket allows different waste mass sum
              }
              if(isProtocolNeeded && setProtocolCompleted !== true) {
                return true; // skip validation if protocol is needed
              }

              const netMassNum = parseFloat(netMassValue);
              if (isNaN(netMassNum)) return true; // handled by required/type validators

              const wastesSum = (wastes ?? []).reduce((sum, w) => sum + (parseFloat(w?.wasteMass) || 0), 0);
              const rejectedWastesSum = (rejectedWastes ?? []).reduce((sum, r) => sum + (parseFloat(r?.wasteMass) || 0), 0);
              const totalSum = wastesSum + rejectedWastesSum;

              return Math.abs(totalSum - netMassNum) <= MASS_SUM_TOLERANCE;
            }
          ),
        exchangeType: Yup.string().nullable().required("Typ rozładunku jest wymagany")
          .test(
            "loading-waste-exchange-defined",
            "Załadunek nie zdefiniowany w towarach",
            function (exchangeTypeValue) {
              if (exchangeTypeValue !== WASTE_EXCHANGE_TYPE.LOADING) {
                return true;
              }

              const { loadingWasteExchangeId } = this.parent ?? {};
              return loadingWasteExchangeId !== null && loadingWasteExchangeId !== undefined;
            }
          ),
        wasteExchange: Yup.object().nullable().when("exchangeType", {
          is: (exchangeType) => exchangeType === WASTE_EXCHANGE_TYPE.CONTAINER_EXCHANGE,
          then: Yup.object().nullable().required("Kontener jest wymagany")
        }),
        wastes: Yup.array()
          .when([ "isProtocolNeeded", "isZeroWeighedTicket" ], {
            is: (isProtocolNeeded, isZeroWeighedTicket) => !isZeroWeighedTicket && isProtocolNeeded && setProtocolCompleted !== true,
            then: Yup.array().of(Yup.object().shape({ wasteMass: Yup.string().nullable() })),
            otherwise: Yup.array().when("isZeroWeighedTicket", {
              is: (isZeroWeighedTicket) => !!isZeroWeighedTicket,
              then: Yup.array().of(zeroWeighedWasteSchema),
              otherwise: Yup.array().of(WasteMassValidator.wasteSchema)
            })
          })
          .test(
            "external-wastes-covered",
            "Wszystkie towary z kwitu zewnętrznego muszą zostać dodane do listy towarów",
            function (wastesValue) {
              const externalWastes = this.parent?.externalData?.wastes;
              if (!externalWastes || externalWastes.length === 0) {
                return true;
              }
              const wasteCodes = (wastesValue ?? []).map((w) => w?.code ?? w?.wasteCode).filter(Boolean);
              const missingCodes = externalWastes
                .map((w) => w?.wasteCode)
                .filter((code) => code && !wasteCodes.includes(code));

              if (missingCodes.length === 0) {
                return true;
              }

              return this.createError({
                message: `Brak towarów o kodach: ${missingCodes.join(", ")} z kwitu magazynowego`
              });
            }
          )
          .test(
            "no-duplicate-waste-entity-id-in-ticket",
            "Towar może zostać dodany tylko raz w obrębie jednego kwitu",
            function (wastesValue) {
              const wasteEntityIds = (wastesValue ?? []).map((w) => w?.wasteEntityId).filter(Boolean);
              const hasDuplicates = wasteEntityIds.some((id, index) => wasteEntityIds.indexOf(id) !== index);
              return !hasDuplicates;
            }
          ),
        rejectedWastes: Yup.array().of(
          Yup.object().shape({
            weightDifference: Yup.object().nullable().required("Różnica w wadze jest wymagana"),
            wasteMass: Yup.number()
              .typeError("Masa jest wymagana")
              .required("Masa jest wymagana")
              .positive("Masa musi być większa od 0")
          })
        )
      })
    )
    //.min(1, "Wymagany co najmniej jeden kwit wagowy")
  });

  return { formControls: formControlsElement, defaultValues, validationSchema };
}

export function createEmptyTicket() {
  return {
    ticketId: null,
    ticketNumber: "",
    netMass: "",
    wastes: [],
    rejectedWastes: [],
  };
}
